/* eslint-disable prettier/prettier */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  TextField,
  MenuItem,
  IconButton,
  Snackbar,
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Pix as PixIcon,
  QrCode2 as BarcodeIcon,
} from '@mui/icons-material';
import styled from 'styled-components';
import QRCode from 'qrcode.react';
import { BASE_API, BASE_S3 } from 'config/consts';

// ============ STYLED COMPONENTS ============

const PageContainer = styled(Box)`
  min-height: 100vh;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
`;

const LogoContainer = styled(Box)`
  padding: 20px 40px;
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 200px;
  min-height: 80px;
`;

const Logo = styled.img`
  max-height: 60px;
  max-width: 180px;
  object-fit: contain;
`;

const ContentWrapper = styled(Box)`
  max-width: 600px;
  width: 100%;
`;

const RazaoSocial = styled(Typography)`
  color: #666;
  font-size: 16px;
  margin-bottom: 8px;
`;

const CNPJ = styled(Typography)`
  color: #333;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 24px;
`;

const SectionTitle = styled(Typography)`
  color: #1976d2;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
`;

const DottedDivider = styled(Box)`
  border-top: 2px dotted #ccc;
  margin: 24px 0;
`;

const ItemsTable = styled(Box)`
  width: 100%;
  margin-bottom: 16px;
`;

const ItemRow = styled(Box)`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #eee;
`;

const TotalRow = styled(Box)`
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  font-weight: 600;
`;

const PaymentMethodsContainer = styled(Box)`
  display: flex;
  justify-content: center;
  gap: 16px;
  margin: 24px 0;
`;

const PaymentMethodButton = styled(Button)`
  min-width: 100px;
  padding: 16px 28px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: #1976d2;
  color: white;
  text-transform: none;
  font-weight: 600;
  font-size: 13px;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.3);

  &:hover {
    background: #1565c0;
    box-shadow: 0 4px 12px rgba(25, 118, 210, 0.4);
  }

  .MuiSvgIcon-root {
    font-size: 28px;
  }
`;

const CardFormContainer = styled(Box)`
  margin-top: 24px;
`;

const CardPreview = styled(Box)`
  background: linear-gradient(135deg, #e0e0e0 0%, #f5f5f5 100%);
  border-radius: 16px;
  padding: 24px;
  margin: 24px auto;
  max-width: 320px;
  aspect-ratio: 1.6;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const ChipIcon = styled(Box)`
  width: 50px;
  height: 40px;
  background: linear-gradient(135deg, #bdbdbd 0%, #9e9e9e 100%);
  border-radius: 8px;
`;

const PixInfoBox = styled(Box)`
  background: #f5f5f5;
  border-radius: 8px;
  padding: 16px;
  margin: 16px 0;
`;

const PixResultContainer = styled(Box)`
  margin-top: 24px;
`;

const ClientInfoBox = styled(Box)`
  display: flex;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const ClientInfoColumn = styled(Box)`
  flex: 1;
`;

const PendingBadge = styled(Box)`
  display: inline-block;
  background: white;
  border: 2px solid #ff9800;
  color: #ff9800;
  padding: 8px 16px;
  border-radius: 4px;
  font-weight: 600;
  font-size: 14px;
`;

const PaidBadge = styled(Box)`
  display: inline-block;
  background: #4caf50;
  border: 2px solid #4caf50;
  color: white;
  padding: 8px 16px;
  border-radius: 4px;
  font-weight: 600;
  font-size: 14px;
`;

const PixButton = styled(Button)`
  background: #1976d2;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  text-transform: none;
  font-weight: 600;

  &:hover {
    background: #1565c0;
  }
`;

const ConfirmButton = styled(Button)`
  background: #1976d2;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  text-transform: none;
  font-weight: 600;

  &:hover {
    background: #1565c0;
  }
`;

const BackButton = styled(Button)`
  background: #757575;
  color: white;
  padding: 12px 32px;
  border-radius: 4px;
  text-transform: none;
  margin-top: 24px;

  &:hover {
    background: #616161;
  }
`;

const SuccessBox = styled(Box)`
  background: #ffeb3b;
  border: 2px solid #333;
  border-radius: 8px;
  padding: 24px;
  margin: 24px 0;
  text-align: left;
`;

const SuccessIcon = styled(Box)`
  width: 80px;
  height: 80px;
  background: #4caf50;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 24px auto;
`;

// ============ COMPONENT ============

const PagamentoPage = (props) => {
  const linkCode = props.params?.linkCode;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState('');
  const [processing, setProcessing] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  // Dados do cartão
  const [cardNumber, setCardNumber] = useState('');
  const [cardHolder, setCardHolder] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvv, setCardCvv] = useState('');
  const [installments, setInstallments] = useState(1);

  useEffect(() => {
    if (linkCode) {
      fetchPaymentLink();
    }
  }, [linkCode]);

  // Auto-selecionar cartão para pagamentos recorrentes
  useEffect(() => {
    if (paymentData?.recurrence_type && paymentData.recurrence_type !== 'single') {
      setSelectedMethod('cartao');
    }
  }, [paymentData?.recurrence_type]);

  const fetchPaymentLink = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_API}/payment-links/public/${linkCode}`);

      if (response.data?.success) {
        setPaymentData(response.data.payment_link);
      } else {
        setError('Link de pagamento não encontrado ou expirado.');
      }
    } catch (err) {
      console.error('Erro ao buscar link:', err);
      setError(err.response?.data?.message || 'Erro ao carregar link de pagamento.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatCardNumber = (value) => {
    const v = value.replace(/\D/g, '').slice(0, 16);
    const parts = [];
    for (let i = 0; i < v.length; i += 4) {
      parts.push(v.slice(i, i + 4));
    }
    return parts.join(' ');
  };

  const formatExpiry = (value) => {
    const v = value.replace(/\D/g, '').slice(0, 4);
    if (v.length >= 2) {
      return v.slice(0, 2) + '/' + v.slice(2);
    }
    return v;
  };

  const getLogoUrl = (logo) => {
    if (!logo) return null;
    if (logo.startsWith('http')) return logo;
    return `${BASE_S3}/${logo}`;
  };

  const copyPixCode = () => {
    if (paymentResult?.pix_code) {
      navigator.clipboard.writeText(paymentResult.pix_code);
      setSnackbar({ open: true, message: 'Código PIX copiado!' });
    }
  };

  const [checkingStatus, setCheckingStatus] = useState(false);

  const checkPaymentStatus = async ({ silent = false } = {}) => {
    if (!silent) {
      setCheckingStatus(true);
    }
    try {
      const response = await axios.get(`${BASE_API}/payment-links/check-status/${linkCode}`);

      if (response.data?.success) {
        if (response.data.paid || response.data.status === 'paid') {
          setSnackbar({ open: true, message: 'Pagamento confirmado!' });
          // Atualizar o resultado do pagamento
          setPaymentResult(prev => ({
            ...prev,
            status: 'paid',
            paid: true,
          }));
          // Atualizar dados do link
          setPaymentData(prev => ({
            ...prev,
            status: 'paid',
          }));
        } else if (!silent) {
          setSnackbar({ open: true, message: response.data.message || 'Aguardando pagamento...' });
        }
      } else if (!silent) {
        setSnackbar({ open: true, message: 'Não foi possível verificar o status' });
      }
    } catch (err) {
      console.error('Erro ao verificar status:', err);
      if (!silent) {
        setSnackbar({ open: true, message: 'Erro ao verificar status do pagamento' });
      }
    } finally {
      if (!silent) {
        setCheckingStatus(false);
      }
    }
  };

  useEffect(() => {
    const shouldAutoCheckPix =
      selectedMethod === 'pix' &&
      paymentResult?.success &&
      !paymentResult?.paid &&
      paymentData?.status !== 'paid';

    if (!shouldAutoCheckPix) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      checkPaymentStatus({ silent: true });
    }, 5000);

    return () => clearInterval(intervalId);
  }, [
    selectedMethod,
    paymentResult?.success,
    paymentResult?.paid,
    paymentData?.status,
    linkCode,
  ]);

  const getRecurrenceLabel = (type) => {
    const labels = {
      single: 'Compra única',
      weekly: 'Semanal',
      biweekly: 'Quinzenal',
      monthly: 'Mensal',
      bimonthly: 'Bimestral',
      quarterly: 'Trimestral',
      semiannual: 'Semestral',
      annual: 'Anual',
    };
    return labels[type] || type;
  };

  const isRecurring = paymentData?.recurrence_type && paymentData.recurrence_type !== 'single';

  const getInstallmentOptions = () => {
    const amount = paymentData?.amount || 0;
    const maxInstallments = paymentData?.allow_installments
      ? Math.min(Math.max(parseInt(paymentData?.max_installments, 10) || 12, 1), 12)
      : 1;
    const options = [];

    for (let i = 1; i <= maxInstallments; i++) {
      const installmentValue = amount / i;
      const total = amount; // Sem juros por enquanto
      options.push({
        value: i,
        label: `${i}x de ${formatCurrency(installmentValue)} - Total: ${formatCurrency(total)}`,
      });
    }
    return options;
  };

  const handlePayment = async () => {
    if (!selectedMethod) {
      setError('Selecione uma forma de pagamento');
      return;
    }

    // Validar dados do cartão
    if (selectedMethod === 'cartao') {
      if (!cardNumber || cardNumber.replace(/\D/g, '').length < 16) {
        setError('Número do cartão inválido');
        return;
      }
      if (!cardHolder || cardHolder.length < 3) {
        setError('Nome do titular inválido');
        return;
      }
      if (!cardExpiry || cardExpiry.replace(/\D/g, '').length < 4) {
        setError('Validade inválida');
        return;
      }
      if (!cardCvv || cardCvv.length < 3) {
        setError('CVV inválido');
        return;
      }
    }

    setError(null);
    setProcessing(true);

    try {
      const payload = {
        payment_method: selectedMethod,
      };

      if (selectedMethod === 'cartao') {
        payload.card_number = cardNumber.replace(/\D/g, '');
        payload.card_holder_name = cardHolder;
        payload.card_expiration_date = cardExpiry.replace(/\D/g, '');
        payload.card_cvv = cardCvv;
        payload.installments = installments;
      }

      const response = await axios.post(`${BASE_API}/payment-links/process/${linkCode}`, payload);

      if (response.data?.success) {
        setPaymentResult(response.data);
      } else {
        setError(response.data?.message || 'Erro ao processar pagamento');
      }
    } catch (err) {
      console.error('Erro ao processar pagamento:', err);
      setError(err.response?.data?.message || 'Erro ao processar pagamento');
    } finally {
      setProcessing(false);
    }
  };

  // ============ RENDER LOADING ============
  if (loading) {
    return (
      <PageContainer>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Carregando...</Typography>
      </PageContainer>
    );
  }

  // ============ RENDER ERROR ============
  if (error && !paymentData) {
    return (
      <PageContainer>
        <Typography variant="h5" color="error" gutterBottom>
          Ops!
        </Typography>
        <Typography color="text.secondary">
          {error}
        </Typography>
      </PageContainer>
    );
  }

  // ============ RENDER HEADER ============
  const renderHeader = () => (
    <>
      <LogoContainer>
        {paymentData?.station?.logo ? (
          <Logo src={getLogoUrl(paymentData.station.logo)} alt="Logo" />
        ) : (
          <Typography variant="h5" fontWeight={500}>Logo da Estação</Typography>
        )}
      </LogoContainer>

      {paymentData?.station?.name && (
        <RazaoSocial>
          Razão Social: {paymentData.station.name}
        </RazaoSocial>
      )}

      {paymentData?.station?.cnpj && (
        <CNPJ>CNPJ: {paymentData.station.cnpj}</CNPJ>
      )}
    </>
  );

  // ============ RENDER SUCCESS - CARTÃO ============
  if (paymentResult?.success && selectedMethod === 'cartao') {
    return (
      <PageContainer>
        <ContentWrapper>
          {renderHeader()}

          <DottedDivider />

          <SuccessBox>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              {paymentData?.recurrence_type && paymentData.recurrence_type !== 'single'
                ? 'Assinatura recorrente criada!'
                : 'Recebemos o seu pagamento!'}
            </Typography>
            <Typography>
              {paymentData?.recurrence_type && paymentData.recurrence_type !== 'single'
                ? 'Sua assinatura foi ativada. O cartão será cobrado automaticamente a cada período.'
                : 'Após o processamento sua compra estará disponível em "Minhas Compras".'}
            </Typography>
          </SuccessBox>

          <SuccessIcon>
            <CheckIcon sx={{ fontSize: 48, color: 'white' }} />
          </SuccessIcon>
        </ContentWrapper>
      </PageContainer>
    );
  }

  // ============ RENDER PAGO - PIX / BOLETO ============
  const isPaid = paymentData?.status === 'paid' || paymentResult?.paid;
  if (paymentResult?.success && (selectedMethod === 'pix' || selectedMethod === 'boleto') && isPaid) {
    return (
      <PageContainer>
        <ContentWrapper>
          {renderHeader()}

          <DottedDivider />

          <SuccessBox>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Recebemos o seu pagamento!
            </Typography>
            <Typography>
              Sua inscrição foi confirmada.
            </Typography>
          </SuccessBox>

          <SuccessIcon>
            <CheckIcon sx={{ fontSize: 48, color: 'white' }} />
          </SuccessIcon>
        </ContentWrapper>
      </PageContainer>
    );
  }

  // ============ RENDER SUCCESS - PIX ============
  if (paymentResult?.success && selectedMethod === 'pix') {
    return (
      <PageContainer>
        <ContentWrapper>
          {renderHeader()}

          <DottedDivider />

          <ClientInfoBox>
            <ClientInfoColumn>
              <Typography variant="caption" color="text.secondary">Cliente</Typography>
              <Typography variant="h6" color="primary" fontWeight={600}>
                {paymentData?.recipient_name}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>CPF/CNPJ</Typography>
              <Typography color="primary" fontWeight={500}>
                {paymentData?.recipient_cpf || '-'}
              </Typography>
              {paymentData?.recipient_email && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>E-mail</Typography>
                  <Typography color="text.primary" fontSize={14}>
                    {paymentData.recipient_email}
                  </Typography>
                </>
              )}
              {paymentData?.recipient_phone && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>Telefone</Typography>
                  <Typography color="text.primary" fontSize={14}>
                    {paymentData.recipient_phone}
                  </Typography>
                </>
              )}
            </ClientInfoColumn>
            <ClientInfoColumn sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary">Vencimento</Typography>
              <Typography variant="h6" fontWeight={600}>
                {formatDate(paymentData?.expires_at)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>Valor Total</Typography>
              <Typography variant="h6" color="primary" fontWeight={600}>
                {formatCurrency(paymentData?.amount)}
              </Typography>
            </ClientInfoColumn>
          </ClientInfoBox>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            {paymentData?.status === 'paid' || paymentResult?.paid ? (
              <PaidBadge>PAGO</PaidBadge>
            ) : (
              <PendingBadge>PENDENTE DE PAGAMENTO</PendingBadge>
            )}
          </Box>

          <DottedDivider />

          <SectionTitle>Pague com PIX</SectionTitle>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            Pagamento PIX por QR Code
          </Typography>

          <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start', mt: 2 }}>
            <Box sx={{ background: 'white', p: 2, border: '1px solid #e0e0e0' }}>
              {paymentResult.pix_code ? (
                <QRCode value={paymentResult.pix_code} size={150} level="M" />
              ) : (
                <Typography color="error">QR Code não disponível</Typography>
              )}
            </Box>

            <Box sx={{ flex: 1 }}>
              <PixButton
                variant="contained"
                startIcon={<CopyIcon />}
                onClick={copyPixCode}
                fullWidth
                sx={{ mb: 2 }}
              >
                PIX - COPIA E COLA
              </PixButton>

              <ConfirmButton
                variant="contained"
                startIcon={checkingStatus ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
                fullWidth
                onClick={checkPaymentStatus}
                disabled={checkingStatus}
              >
                {checkingStatus ? 'VERIFICANDO...' : 'CONFIRMAR PAGAMENTO'}
              </ConfirmButton>
            </Box>
          </Box>
        </ContentWrapper>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          message={snackbar.message}
        />
      </PageContainer>
    );
  }

  // ============ RENDER SUCCESS - BOLETO ============
  if (paymentResult?.success && selectedMethod === 'boleto') {
    return (
      <PageContainer>
        <ContentWrapper>
          {renderHeader()}

          <DottedDivider />

          <ClientInfoBox>
            <ClientInfoColumn>
              <Typography variant="caption" color="text.secondary">Cliente</Typography>
              <Typography variant="h6" color="primary" fontWeight={600}>
                {paymentData?.recipient_name}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>CPF/CNPJ</Typography>
              <Typography color="primary" fontWeight={500}>
                {paymentData?.recipient_cpf || '-'}
              </Typography>
              {paymentData?.recipient_email && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>E-mail</Typography>
                  <Typography color="text.primary" fontSize={14}>
                    {paymentData.recipient_email}
                  </Typography>
                </>
              )}
              {paymentData?.recipient_phone && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>Telefone</Typography>
                  <Typography color="text.primary" fontSize={14}>
                    {paymentData.recipient_phone}
                  </Typography>
                </>
              )}
            </ClientInfoColumn>
            <ClientInfoColumn sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary">Vencimento</Typography>
              <Typography variant="h6" fontWeight={600}>
                {formatDate(paymentData?.expires_at)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>Valor Total</Typography>
              <Typography variant="h6" color="primary" fontWeight={600}>
                {formatCurrency(paymentData?.amount)}
              </Typography>
            </ClientInfoColumn>
          </ClientInfoBox>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            {paymentData?.status === 'paid' || paymentResult?.paid ? (
              <PaidBadge>PAGO</PaidBadge>
            ) : (
              <PendingBadge>PENDENTE DE PAGAMENTO</PendingBadge>
            )}
          </Box>

          <DottedDivider />

          <SectionTitle>Boleto Gerado</SectionTitle>

          {paymentResult.boleto_barcode && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Código de Barras:
              </Typography>
              <Box sx={{
                background: '#f5f5f5',
                p: 2,
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: 12,
                wordBreak: 'break-all',
                position: 'relative'
              }}>
                {paymentResult.boleto_barcode}
                <IconButton
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText(paymentResult.boleto_barcode);
                    setSnackbar({ open: true, message: 'Código copiado!' });
                  }}
                  sx={{ position: 'absolute', right: 4, top: 4 }}
                >
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
            <PixButton
              variant="contained"
              fullWidth
              onClick={() => window.open(paymentResult.boleto_url, '_blank')}
            >
              VISUALIZAR BOLETO
            </PixButton>

            <ConfirmButton
              variant="contained"
              fullWidth
              onClick={() => {
                const pdfUrl = paymentResult.boleto_url.includes('?')
                  ? `${paymentResult.boleto_url}&format=pdf`
                  : `${paymentResult.boleto_url}?format=pdf`;
                window.open(pdfUrl, '_blank');
              }}
            >
              BAIXAR BOLETO (PDF)
            </ConfirmButton>
          </Box>
        </ContentWrapper>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          message={snackbar.message}
        />
      </PageContainer>
    );
  }

  // ============ RENDER MAIN FORM ============
  return (
    <PageContainer>
      <ContentWrapper>
        {renderHeader()}

        <SectionTitle>Itens da compra</SectionTitle>

        <ItemsTable>
          <ItemRow>
            <Typography variant="body2" color="text.secondary">Descrição</Typography>
            <Typography variant="body2" color="text.secondary">Valor</Typography>
          </ItemRow>
          <ItemRow>
            <Box>
              <Typography>{paymentData?.service_name || 'Link de Pagamento'}</Typography>
            </Box>
            <Typography>{formatCurrency(paymentData?.amount)}</Typography>
          </ItemRow>
        </ItemsTable>

        <TotalRow>
          <Typography>Subtotal</Typography>
          <Typography>{formatCurrency(paymentData?.amount)}</Typography>
        </TotalRow>
        <TotalRow>
          <Typography>Desconto</Typography>
          <Typography>R$ 0,00</Typography>
        </TotalRow>
        <TotalRow>
          <Typography variant="h6" fontWeight={700}>Total</Typography>
          <Typography variant="h6" fontWeight={700} color="primary">
            {formatCurrency(paymentData?.amount)}
          </Typography>
        </TotalRow>

        <DottedDivider />

        {paymentData?.recurrence_type && paymentData.recurrence_type !== 'single' && (
          <Box sx={{
            backgroundColor: '#fff3e0',
            border: '1px solid #ff9800',
            borderRadius: '8px',
            padding: '12px 16px',
            mb: 2,
            textAlign: 'center',
          }}>
            <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 600 }}>
              Pagamento recorrente: {
                { weekly: 'Semanal', biweekly: 'Quinzenal', monthly: 'Mensal',
                  bimonthly: 'Bimestral', quarterly: 'Trimestral',
                  semiannual: 'Semestral', annual: 'Anual',
                }[paymentData.recurrence_type] || paymentData.recurrence_type
              }
            </Typography>
            <Typography variant="caption" sx={{ color: '#bf360c' }}>
              Seu cartão será cobrado automaticamente a cada período. Apenas cartão de crédito.
            </Typography>
          </Box>
        )}

        <Typography variant="body1" textAlign="center" sx={{ mb: 2 }}>
          {isRecurring
            ? 'Informe os dados do cartão de crédito para ativar sua assinatura.'
            : 'Escolha uma forma de pagamento.'}
        </Typography>

        {error && (
          <Typography color="error" textAlign="center" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        <PaymentMethodsContainer>
          {paymentData?.payment_methods?.includes('cartao') && (
            <PaymentMethodButton
              onClick={() => setSelectedMethod('cartao')}
            >
              <CreditCardIcon />
              <span>CRÉDITO</span>
            </PaymentMethodButton>
          )}

          {paymentData?.payment_methods?.includes('pix') &&
            !(paymentData?.recurrence_type && paymentData.recurrence_type !== 'single') && (
            <PaymentMethodButton
              onClick={() => setSelectedMethod('pix')}
            >
              <PixIcon />
              <span>PIX</span>
            </PaymentMethodButton>
          )}

          {paymentData?.payment_methods?.includes('boleto') &&
            !(paymentData?.recurrence_type && paymentData.recurrence_type !== 'single') && (
            <PaymentMethodButton
              onClick={() => setSelectedMethod('boleto')}
            >
              <BarcodeIcon />
              <span>BOLETO</span>
            </PaymentMethodButton>
          )}
        </PaymentMethodsContainer>

        {/* Formulário de Cartão */}
        {selectedMethod === 'cartao' && (
          <CardFormContainer>
            <TextField
              fullWidth
              label="Número do cartão"
              placeholder="9999 9999 9999 9999"
              value={cardNumber}
              onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
              sx={{ mb: 2 }}
              inputProps={{ maxLength: 19, autoComplete: 'off' }}
              autoComplete="off"
              size="small"
            />

            <TextField
              fullWidth
              label="Nome impresso no cartão"
              placeholder="Nome do titular do cartão"
              value={cardHolder}
              onChange={(e) => setCardHolder(e.target.value.toUpperCase())}
              sx={{ mb: 2 }}
              inputProps={{ autoComplete: 'off' }}
              autoComplete="off"
              size="small"
            />

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                label="Validade"
                placeholder="MM/AA"
                value={cardExpiry}
                onChange={(e) => setCardExpiry(formatExpiry(e.target.value))}
                inputProps={{ maxLength: 5, autoComplete: 'off' }}
                autoComplete="off"
                size="small"
                sx={{ flex: 1 }}
              />
              <TextField
                label="CVV"
                placeholder="CVV"
                value={cardCvv}
                onChange={(e) => setCardCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
                inputProps={{ maxLength: 4, autoComplete: 'off' }}
                autoComplete="off"
                type="password"
                size="small"
                sx={{ flex: 1 }}
              />
            </Box>

            {/* Card Preview */}
            <CardPreview>
              <ChipIcon />
              <Box>
                <Typography sx={{ letterSpacing: 2, color: '#666', fontSize: 14 }}>
                  {cardNumber || '•••• •••• •••• ••••'}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography sx={{ color: '#666', fontSize: 12 }}>
                    {cardHolder || 'NOME COMPLETO'}
                  </Typography>
                  <Typography sx={{ color: '#666', fontSize: 12 }}>
                    {cardExpiry || 'MM/AA'}
                  </Typography>
                </Box>
              </Box>
            </CardPreview>

            {paymentData?.allow_installments && !isRecurring && (
              <TextField
                select
                fullWidth
                label="Parcelas"
                value={installments}
                onChange={(e) => setInstallments(e.target.value)}
                size="small"
                sx={{ mb: 2 }}
              >
                {getInstallmentOptions().map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handlePayment}
              disabled={processing}
              sx={{
                background: '#4caf50',
                py: 1.5,
                '&:hover': { background: '#43a047' },
              }}
              startIcon={<CreditCardIcon />}
            >
              {processing ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'PAGAR AGORA'}
            </Button>

            <Typography variant="caption" display="block" textAlign="center" sx={{ mt: 2, color: '#666' }}>
              Ambiente criptografado e 100% seguro
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
              <Typography variant="caption" sx={{
                background: '#4caf50',
                color: 'white',
                px: 2,
                py: 0.5,
                borderRadius: 1,
                display: 'inline-block',
                fontWeight: 600
              }}>
                🔒 SITE SEGURO
              </Typography>
              <Typography variant="caption" sx={{
                background: '#ff9800',
                color: 'white',
                px: 2,
                py: 0.5,
                borderRadius: 1,
                display: 'inline-block',
                fontWeight: 600
              }}>
                SSL CERTIFICADO
              </Typography>
            </Box>
          </CardFormContainer>
        )}

        {/* Informações do PIX */}
        {selectedMethod === 'pix' && (
          <PixInfoBox>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              PAGAMENTO POR PIX
            </Typography>
            <Typography variant="body2" color="text.secondary">
              1 - Pagamento em segundos, sem complicação.<br />
              2 - Basta escanear, com o aplicativo do seu banco, o QRCode que iremos gerar para sua compra.<br />
              3 - PIX foi desenvolvido pelo Banco Central para facilitar suas compras e é 100% seguro.
            </Typography>

            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handlePayment}
              disabled={processing}
              sx={{
                mt: 3,
                background: '#4caf50',
                py: 1.5,
                '&:hover': { background: '#43a047' },
              }}
              startIcon={<Box component="span">⊞</Box>}
            >
              {processing ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'GERAR QR CODE PIX'}
            </Button>
          </PixInfoBox>
        )}

        {/* Informações do Boleto */}
        {selectedMethod === 'boleto' && (
          <PixInfoBox>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              PAGAMENTO POR BOLETO
            </Typography>
            <Typography variant="body2" color="text.secondary">
              1 - O boleto será gerado e você poderá pagar em qualquer banco ou lotérica.<br />
              2 - O prazo de compensação é de até 3 dias úteis.<br />
              3 - Após o pagamento, sua compra será liberada automaticamente.
            </Typography>

            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handlePayment}
              disabled={processing}
              sx={{
                mt: 3,
                background: '#4caf50',
                py: 1.5,
                '&:hover': { background: '#43a047' },
              }}
            >
              {processing ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'GERAR BOLETO'}
            </Button>
          </PixInfoBox>
        )}

        <Box sx={{ textAlign: 'center' }}>
          <BackButton>
            VOLTAR AO INÍCIO
          </BackButton>
        </Box>
      </ContentWrapper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </PageContainer>
  );
};

export default PagamentoPage;
